"use client";

import React, { useState, useEffect, useId, useRef, ReactNode, useCallback } from "react";
import Link from "next/link";
import clsx from "clsx";
import {
  ACCORDION_PANEL_BG,
  AccordionChevron,
  accordionBody,
  accordionBodyClosed,
  accordionBodyOpen,
  accordionCard,
  accordionDivider,
  accordionHeader,
  accordionPanel,
  accordionPanelClip,
  accordionPanelClosed,
  accordionPanelOpen,
  useAccordionScrollAnchor,
} from "@/components/common/accordion";

export type TabState = "default" | "hovered" | "selected";

interface TabAtomProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  state?: TabState;
  active?: boolean;
  children?: React.ReactNode;
}

/**
 * Atomic Tab component matching the design-system spec
 * (Default / Hovered / Selected). For full tabbed UIs use the `Tabs` component
 * exported as default.
 */
export const Tab: React.FC<TabAtomProps> = ({ state, active, className, children, ...rest }) => {
  const resolvedState: TabState = state ?? (active ? "selected" : "default");
  return (
    <button
      type="button"
      className={clsx(
        "inline-flex items-center justify-center py-2 cursor-pointer transition-colors",
        "font-[var(--font-avenir)] font-bold text-[20px] leading-[25px] tracking-[0.2px] text-center whitespace-nowrap",
        resolvedState === "selected" &&
          "border-b-2 border-solid border-primary-600 text-primary-600",
        resolvedState === "hovered" && "text-text-highlight",
        resolvedState === "default" && "text-neutral-500 hover:text-text-highlight",
        className
      )}
      {...rest}
    >
      {children}
    </button>
  );
};

interface TabSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

/** Horizontal list of `Tab` atoms with the bottom divider. */
export const TabSection: React.FC<TabSectionProps> = ({ className, children, ...rest }) => (
  <div
    className={clsx("flex items-center gap-8 border-b border-solid border-neutral-500", className)}
    {...rest}
  >
    {children}
  </div>
);

export const TAB_STYLE = {
  STYLE_1: 1,
  STYLE_2: 2,
} as const;

export const TAB_ORIENTATION = {
  HORIZONTAL: "horizontal",
  VERTICAL: "vertical",
} as const;

export interface TabItem {
  id: string;
  name: string;
  value: string;
  content?: ReactNode;
  disabled?: boolean;
  number?: number;
  href?: string;
}

interface TabsProps {
  tabs: TabItem[];
  defaultValue?: string;
  currentValue?: string;
  onChange?: (value: string) => void;
  style?: typeof TAB_STYLE.STYLE_1 | typeof TAB_STYLE.STYLE_2;
  orientation?: typeof TAB_ORIENTATION.HORIZONTAL | typeof TAB_ORIENTATION.VERTICAL;
  customClassName?: string;
  showContent?: boolean;
  useRouter?: boolean;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  hideTabHeaders?: boolean;
  /** Vertical layout only: show the download icon in the content header. */
  showDownload?: boolean;
  /** Vertical layout only: handler for the download icon. */
  onDownloadClick?: () => void;
  /** Vertical layout only: width of the left step-list column (default 438px). */
  verticalListWidth?: string;
}

interface TabWrapperProps {
  children: ReactNode;
  item: TabItem;
  isDisabled: boolean;
  className: string;
  onTabChange: (value: string) => void;
  useRouter: boolean;
  style?: React.CSSProperties;
  active?: boolean;
  /**
   * Fired the moment a routed tab is tapped, before the navigation resolves.
   * The `<div>` branch has no use for it — `onTabChange` already runs
   * synchronously there.
   */
  onNavigate?: (value: string) => void;
}

const TabWrapper: React.FC<TabWrapperProps> = ({
  children,
  item,
  isDisabled,
  className,
  onTabChange,
  useRouter,
  style,
  active,
  onNavigate,
}) => {
  // Mark the active tab so the header can scroll it into view and for a11y.
  const activeAttrs = active ? { "aria-current": "page" as const, "data-active": "true" } : {};

  if (useRouter && item.href && !isDisabled) {
    return (
      <Link
        href={item.href}
        className={className}
        style={style}
        onClick={() => onNavigate?.(item.value)}
        {...activeAttrs}
      >
        {children}
      </Link>
    );
  }

  return (
    <div
      onClick={() => !isDisabled && onTabChange(item.value)}
      className={className}
      style={style}
      {...activeAttrs}
    >
      {children}
    </div>
  );
};

const PrevIcon = () => (
  <svg width="7" height="11" viewBox="0 0 7 11" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2.00684 5.46191L5.95508 9.41016L5.24805 10.1172L0.723633 5.59375L0.838867 5.47852L0.707031 5.34766L5.34766 0.707031L6.05469 1.41406L2.00684 5.46191Z"
      fill="currentColor"
    />
  </svg>
);

const NextIcon = () => (
  <svg width="7" height="11" viewBox="0 0 7 11" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4.75488 5.3623L0.806641 1.41406L1.51367 0.707031L6.03809 5.23047L5.92285 5.3457L6.05469 5.47656L1.41406 10.1172L0.707031 9.41016L4.75488 5.3623Z"
      fill="currentColor"
    />
  </svg>
);

const DownloadIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M8 1.5V10.5M8 10.5L4 6.5M8 10.5L12 6.5M2 14H14"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Tabs: React.FC<TabsProps> = ({
  tabs,
  defaultValue,
  currentValue,
  onChange,
  style = TAB_STYLE.STYLE_1,
  orientation = TAB_ORIENTATION.HORIZONTAL,
  customClassName,
  showContent = true,
  useRouter = false,
  autoPlay = false,
  autoPlayInterval = 5000,
  hideTabHeaders = false,
  showDownload = false,
  onDownloadClick,
  verticalListWidth = "438px",
}) => {
  const theme = "dark";
  const [curTab, setCurTab] = useState<string>(defaultValue || tabs[0]?.value || "");
  const [isPaused, setIsPaused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Horizontal STYLE_1 headers scroll on narrow screens; keep the active tab
  // centred in view (matches the products/HackGrid tab bar).
  const headerScrollRef = useRef<HTMLDivElement>(null);
  // Ties each mobile accordion header to the panel it controls.
  const uid = useId();
  // Vertical layout below `lg`: which step row is expanded. Tracked separately
  // from the selection (and starting at null, i.e. every row collapsed) exactly
  // like TabbedContent, so the desktop panel and the diagrams wired to
  // `currentValue` keep a non-nullable step.
  const [openRow, setOpenRow] = useState<string | null>(null);
  const syncedValue = useRef<string | null>(null);
  // Holds the tapped row in place while the previously open one collapses.
  const anchorRow = useAccordionScrollAnchor();
  // The row that carries the horizontal tabs, and the measured box of the
  // sliding underline inside it. `null` until the first measurement, so the bar
  // is never painted at a position it hasn't been told yet.
  const tabRowRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState<{ x: number; w: number } | null>(null);
  // Which tab the header PAINTS as selected, tracked apart from the tab the
  // route/props actually name. A routed tab hands off to Next and the two only
  // reconcile once the new payload lands — measured at ~180ms — during which
  // painting the real value left the tap with no feedback whatsoever.
  const [pendingValue, setPendingValue] = useState<string | null>(null);

  const activeValue = currentValue !== undefined ? currentValue : curTab;
  const displayValue = pendingValue ?? activeValue;

  const handleTabChange = useCallback(
    (value: string) => {
      if (!useRouter) {
        setCurTab(value);
      }
      setAnimationKey((prev) => prev + 1);
      onChange?.(value);

      // Pause auto-play on user interaction
      if (autoPlay) {
        setIsPaused(true);
        if (timerRef.current) clearInterval(timerRef.current);
        if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);

        pauseTimeoutRef.current = setTimeout(() => {
          setIsPaused(false);
        }, 8000);
      }
    },
    [autoPlay, onChange, useRouter]
  );

  // Auto-play effect
  useEffect(() => {
    if (!autoPlay || isPaused || isHovered) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    timerRef.current = setInterval(() => {
      const enabledTabs = tabs.filter((tab) => !tab.disabled);
      const currentIndex = enabledTabs.findIndex((tab) => tab.value === activeValue);
      const nextIndex = (currentIndex + 1) % enabledTabs.length;
      handleTabChange(enabledTabs[nextIndex].value);
    }, autoPlayInterval);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [autoPlay, isPaused, isHovered, activeValue, tabs, autoPlayInterval, handleTabChange]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    };
  }, []);

  // Sync with external currentValue
  useEffect(() => {
    if (currentValue && currentValue !== curTab) {
      setCurTab(currentValue);
      setAnimationKey((prev) => prev + 1);
    }
  }, [currentValue, curTab]);

  // The route arrived (or the parent moved the selection some other way), so the
  // painted value and the real one agree again.
  useEffect(() => {
    setPendingValue(null);
  }, [currentValue]);

  // A navigation the reader abandons never changes `currentValue`, which would
  // leave the underline parked on a tab they never reached. Time it out.
  useEffect(() => {
    if (pendingValue === null) return;
    const timer = setTimeout(() => setPendingValue(null), 2000);
    return () => clearTimeout(timer);
  }, [pendingValue]);

  // Bring the active tab into view when the horizontal header overflows and
  // scrolls (no-op when everything fits, e.g. on desktop). Keyed on the painted
  // value so the scroll starts with the tap rather than after the route lands.
  useEffect(() => {
    const container = headerScrollRef.current;
    if (!container) return;
    const active = container.querySelector<HTMLElement>('[data-active="true"]');
    if (!active) return;
    const cRect = container.getBoundingClientRect();
    const aRect = active.getBoundingClientRect();
    const delta = aRect.left - cRect.left - (container.clientWidth - active.clientWidth) / 2;
    container.scrollBy({ left: delta, behavior: "smooth" });
  }, [displayValue]);

  /**
   * Measure the box the sliding underline should occupy.
   *
   * `offsetLeft` is read against the tab row (which is `relative`, so it is the
   * links' `offsetParent`) rather than the viewport — that keeps the number
   * independent of how far the row is scrolled sideways, so the bar cannot drift
   * when a narrow header scrolls the active tab into the middle.
   *
   * Deliberately `useEffect`, not `useLayoutEffect`: this component is
   * server-rendered and the layout variant would warn. The cost is that the bar
   * appears one frame after paint, which nothing can see — the whole tab row
   * starts behind a 150ms-delayed fade-in on every page that uses it.
   */
  useEffect(() => {
    const row = tabRowRef.current;
    if (!row) return;

    const measure = () => {
      const active = row.querySelector<HTMLElement>('[data-active="true"]');
      if (!active) return;
      const x = active.offsetLeft;
      const w = active.offsetWidth;
      // ResizeObserver fires once on observe; bail out rather than re-render
      // with an identical box.
      setIndicator((prev) => (prev && prev.x === x && prev.w === w ? prev : { x, w }));
    };

    measure();
    // Label widths move when the Avenir face finishes loading and on resize.
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [displayValue, tabs]);

  // A step picked outside the row list — the AIDICS ring, the DRAMA wheel, the
  // desktop pager — should reveal that row on mobile too.
  useEffect(() => {
    if (activeValue === syncedValue.current) return;
    const first = syncedValue.current === null;
    syncedValue.current = activeValue;
    // Skip the initial value: mobile starts with every row collapsed.
    if (!first) setOpenRow(activeValue);
  }, [activeValue]);

  const activeTab = tabs.find((tab) => tab.value === activeValue);
  const activeIndex = tabs.findIndex((tab) => tab.value === activeValue);
  const isVertical = orientation === TAB_ORIENTATION.VERTICAL;

  // Mobile accordion row: tapping the open row collapses it and leaves the
  // selection alone; tapping another opens it and selects that step.
  const handleAccordionClick = (value: string) => {
    if (openRow === value) {
      setOpenRow(null);
      return;
    }
    setOpenRow(value);
    handleTabChange(value);
  };

  const handlePrevTab = () => {
    const enabledTabs = tabs.filter((tab) => !tab.disabled);
    const currentEnabledIndex = enabledTabs.findIndex((tab) => tab.value === activeValue);
    if (currentEnabledIndex > 0) {
      handleTabChange(enabledTabs[currentEnabledIndex - 1].value);
    }
  };

  const handleNextTab = () => {
    const enabledTabs = tabs.filter((tab) => !tab.disabled);
    const currentEnabledIndex = enabledTabs.findIndex((tab) => tab.value === activeValue);
    if (currentEnabledIndex < enabledTabs.length - 1) {
      handleTabChange(enabledTabs[currentEnabledIndex + 1].value);
    }
  };

  const canGoPrev = () => {
    const enabledTabs = tabs.filter((tab) => !tab.disabled);
    const currentEnabledIndex = enabledTabs.findIndex((tab) => tab.value === activeValue);
    return currentEnabledIndex > 0;
  };

  const canGoNext = () => {
    const enabledTabs = tabs.filter((tab) => !tab.disabled);
    const currentEnabledIndex = enabledTabs.findIndex((tab) => tab.value === activeValue);
    return currentEnabledIndex < enabledTabs.length - 1;
  };

  return (
    <div
      className={clsx(
        "w-full tabs-container",
        isVertical
          ? hideTabHeaders
            ? "flex flex-col"
            : // V1 layout — 438px step list, ~62px gutter, panel takes the rest.
              "grid grid-cols-1 lg:grid-cols-[var(--tab-list-w)_minmax(0,1fr)] gap-10 lg:gap-[62px]"
          : "flex flex-col"
      )}
      style={
        isVertical && !hideTabHeaders
          ? ({ "--tab-list-w": verticalListWidth } as React.CSSProperties)
          : undefined
      }
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Vertical headers — two layouts, one per breakpoint:
          • lg+ : the V1 numbered step list feeding the panel column on the right.
          • <lg : the site-wide accordion (see `accordion.tsx`), so the step
                  copy sits under its own row exactly like Core Capabilities. */}
      {!hideTabHeaders && isVertical && (
        <>
          {/* Desktop step list. Styling mirrors the V1
              `.resources-brochures__item`: 20px/28px Avenir at normal weight
              (the active step only changes colour, never weight), a 39px number
              column in neutral grey, and a 45px divider on the right. The number
              keeps its own face: V1 sets `font-family: "AvenirNext"` on
              `.resources-brochures__number`, which is AvenirNext-Bold — so it is
              always bold while the step name stays regular. */}
          <div
            data-steplist="desktop"
            className={clsx(
              // No continuous rule behind the list: each row carries its own
              // 45px divider, so the 15px gaps between them stay empty rather
              // than showing a faint grey line through them.
              "hidden lg:flex lg:flex-col gap-[15px] relative",
              customClassName
            )}
          >
            {tabs?.map((item, index) => {
              const isActive = item?.value === activeValue;
              const isDisabled = item?.disabled || false;

              return (
                <TabWrapper
                  key={item?.id}
                  item={item}
                  isDisabled={isDisabled}
                  onTabChange={handleTabChange}
                  useRouter={useRouter}
                  className={clsx(
                    // `relative` keeps each item's divider painted above the
                    // continuous rule behind the list.
                    "group relative flex w-full min-h-[45px] pt-2 pr-6 text-left border-r transition-colors duration-200",
                    "font-[var(--font-avenir)] text-[20px] leading-[28px]",
                    isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
                    isActive
                      ? "text-primary border-primary"
                      : "text-text-highlight hover:text-primary border-neutral-500"
                  )}
                >
                  <span
                    className={clsx(
                      "shrink-0 w-[39px] font-bold",
                      isActive ? "text-primary" : "text-neutral-500 group-hover:text-primary"
                    )}
                  >
                    {item.number ?? index + 1}
                  </span>
                  <span className="block flex-1 min-w-0">{item?.name}</span>
                </TabWrapper>
              );
            })}
          </div>

          {/* Mobile accordion. Starts fully collapsed, opens one row at a time
              and folds the open row again on tap — same contract as
              TabbedContent. The pager / "Page NN" indicator stays desktop-only:
              the row already names the step. */}
          <div data-accordion="steps" className={clsx(accordionCard, "flex flex-col lg:hidden")}>
            {tabs?.map((item, index) => {
              const isOpen = openRow === item?.value;
              const isDisabled = item?.disabled || false;
              const headerId = `${uid}-step-${item?.id}`;
              const panelId = `${uid}-steppanel-${item?.id}`;

              return (
                <React.Fragment key={item?.id}>
                  <button
                    type="button"
                    id={headerId}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    disabled={isDisabled}
                    onClick={(event) => {
                      anchorRow(event.currentTarget);
                      handleAccordionClick(item.value);
                    }}
                    className={clsx(
                      accordionHeader,
                      index > 0 && accordionDivider,
                      isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
                      isOpen ? "text-primary" : "text-text-default hover:text-text-highlight"
                    )}
                  >
                    <span
                      className={clsx(
                        "shrink-0 w-[22px] font-bold transition-colors duration-200",
                        isOpen ? "text-primary" : "text-neutral-500"
                      )}
                    >
                      {item.number ?? index + 1}
                    </span>
                    <span className="flex-1 min-w-0">{item?.name}</span>
                    <AccordionChevron open={isOpen} />
                  </button>

                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={headerId}
                    inert={!isOpen}
                    className={clsx(
                      accordionPanel,
                      isOpen ? accordionPanelOpen : accordionPanelClosed
                    )}
                    style={{ background: ACCORDION_PANEL_BG }}
                  >
                    <div className={accordionPanelClip}>
                      <div
                        className={clsx(
                          accordionBody,
                          "flex flex-col gap-5",
                          isOpen ? accordionBodyOpen : accordionBodyClosed
                        )}
                      >
                        {item?.content}
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </>
      )}

      {/* Horizontal headers */}
      {!hideTabHeaders &&
        !isVertical &&
        (() => {
          const rows = tabs?.map((item) => {
            const isActive = item?.value === displayValue;
            const isDisabled = item?.disabled || false;

            // Horizontal Styles (Enhanced with animations)
            return (
              <TabWrapper
                key={item?.id}
                item={item}
                isDisabled={isDisabled}
                onTabChange={handleTabChange}
                onNavigate={setPendingValue}
                useRouter={useRouter}
                active={isActive}
                className={clsx(
                  "font-bold flex items-center justify-center relative z-10 transition-all duration-300",
                  isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",

                  // Style 1. whitespace-nowrap keeps each label on one line so the
                  // header scrolls sideways instead of wrapping on narrow screens.
                  // The underline itself is not here — one shared bar below slides
                  // between tabs instead of each tab owning an `::after` that can
                  // only teleport.
                  style === TAB_STYLE.STYLE_1 && [
                    "whitespace-nowrap min-w-[120px] sm:min-w-[84px] h-[38px] sm:h-[42px] px-2 sm:px-4",
                    "text-[16px] sm:text-[18px] lg:text-[20px] font-bold leading-[20px] sm:leading-[22px] lg:leading-[25px] tracking-[0.2px]",
                    "font-[var(--font-avenir)]",
                    isActive ? "text-primary" : "text-neutral-500 hover:text-text-highlight",
                  ],

                  // Style 2
                  style === TAB_STYLE.STYLE_2 && [
                    "px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base",
                    theme === "dark" ? "bg-transparent" : "bg-transparent",
                    isActive &&
                      (theme === "dark"
                        ? "!bg-primary/10 !text-primary border border-primary/20"
                        : "!bg-primary/10 !text-primary"),
                    !isActive && "hover:bg-white/5",
                  ]
                )}
              >
                {item?.name}
              </TabWrapper>
            );
          });

          // Horizontal STYLE_1 headers scroll sideways on narrow screens: an
          // outer scroll container + an inner row that carries the gray divider,
          // so the rule stays flush under the tabs even when a scrollbar shows.
          if (style === TAB_STYLE.STYLE_1) {
            return (
              <div ref={headerScrollRef} className="w-full overflow-x-auto no-scrollbar">
                <div
                  ref={tabRowRef}
                  className={clsx(
                    // `relative` makes this the links' offsetParent, which is what
                    // the underline is measured against.
                    "relative flex flex-nowrap items-center gap-4 sm:gap-6 lg:gap-8 w-max min-w-full border-b border-solid border-neutral-500",
                    customClassName
                  )}
                >
                  {rows}

                  {/* The sliding underline. Sits 1px below the tabs and 3px tall
                      so it fully covers the row's gray divider — no grey peeking.

                      MUST stay the LAST child: every layout using this header
                      passes `[&>a:first-child]:!pl-0` to hug the first tab against
                      the page's left margin, and `a:first-child` stops matching
                      the moment anything is inserted ahead of the links. */}
                  {/* The glow stays put at the resting 10px. An earlier version
                      pulsed it brighter on arrival via a fill-forward child
                      animation, which both stacked a second permanent 10px glow on
                      top of this one and bloomed to a 22px red haze on every
                      switch — the motion carries the arrival on its own. */}
                  {indicator && (
                    <span
                      aria-hidden
                      className="tab-underline pointer-events-none absolute bottom-[-1px] left-0 h-[3px] bg-primary shadow-[0_0_10px_rgba(236,25,56,0.5)]"
                      style={{
                        width: indicator.w,
                        transform: `translateX(${indicator.x}px)`,
                      }}
                    />
                  )}
                </div>
              </div>
            );
          }

          // STYLE_2 pill group — single container.
          return (
            <div
              className={clsx(
                "w-full relative flex gap-[6px] p-[6px] rounded-lg",
                style === TAB_STYLE.STYLE_2 && theme === "dark" && "bg-[#1a1c1f]",
                customClassName
              )}
            >
              {rows}
            </div>
          );
        })()}

      {/* Tab Content */}
      {showContent &&
        activeTab?.content &&
        (isVertical ? (
          /* Vertical content panel — V1 `.resources-brochures__content`: the
             page indicator / title block sits 12px above the description card
             and the pager sits vertically centred against it. */
          <div className="hidden lg:flex flex-col gap-3">
            {/* Header: page indicator + title + controls */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-col">
                <div className="flex items-baseline gap-1 font-[var(--font-avenir)] text-[12px] leading-[18px]">
                  <span className="text-neutral-500">Page</span>
                  <span className="text-primary font-semibold">
                    {String(activeIndex + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3
                  key={`title-${animationKey}`}
                  className="text-primary font-[var(--font-avenir)] text-[20px] leading-[30px] content-title"
                >
                  {activeTab.name}
                </h3>
              </div>

              {/* Pager controls as in V1: 30×30 rounded squares (r=3) on a
                  #1B2026 fill, laid out as [download] gap-35 [prev gap-5 next].
                  Download glyph is primary red; prev/next chevrons are white. */}
              <div className="flex items-center gap-[35px] shrink-0">
                {showDownload && (
                  <button
                    type="button"
                    aria-label="Download"
                    onClick={onDownloadClick}
                    className="size-[30px] rounded-[3px] bg-[#1B2026] flex items-center justify-center text-primary hover:opacity-80 transition-opacity cursor-pointer"
                  >
                    <DownloadIcon />
                  </button>
                )}
                <div className="flex items-center gap-[5px]">
                  <button
                    type="button"
                    onClick={handlePrevTab}
                    disabled={!canGoPrev()}
                    aria-label="Previous step"
                    className={clsx(
                      "size-[30px] rounded-[3px] bg-[#1B2026] flex items-center justify-center transition-colors",
                      !canGoPrev()
                        ? "text-neutral-500 opacity-50 cursor-not-allowed"
                        : "text-white hover:text-primary cursor-pointer"
                    )}
                  >
                    <PrevIcon />
                  </button>
                  <button
                    type="button"
                    onClick={handleNextTab}
                    disabled={!canGoNext()}
                    aria-label="Next step"
                    className={clsx(
                      "size-[30px] rounded-[3px] bg-[#1B2026] flex items-center justify-center transition-colors",
                      !canGoNext()
                        ? "text-neutral-500 opacity-50 cursor-not-allowed"
                        : "text-white hover:text-primary cursor-pointer"
                    )}
                  >
                    <NextIcon />
                  </button>
                </div>
              </div>
            </div>

            {/* Body — V1 `.resources-brochures__text-content`: no radius,
                20px/25px padding, copy 18px/27px justified with 30px between
                paragraphs. Fill is the shared accordion panel gradient and, like
                the accordion, it carries no drop shadow — V1's 2/2/6 black
                shadow read as a dark rim next to the mobile panel.
                V1 runs the card as a slick slider at its defaults: the track
                slides sideways in 300ms `ease`, and the track keeps the height of
                the longest step (`items-start`, so each card still ends with its
                own copy) rather than resizing as steps change. */}
            <div className="overflow-hidden">
              <div
                className="flex items-start transition-transform duration-300 ease-[ease] motion-reduce:transition-none"
                style={{
                  transform: `translateX(-${Math.max(activeIndex, 0) * 100}%)`,
                }}
              >
                {tabs.map((tab, index) => (
                  <div
                    key={tab.id}
                    aria-hidden={index !== activeIndex}
                    className="w-full shrink-0 px-[25px] py-5 rounded-[4px]"
                    style={{ background: ACCORDION_PANEL_BG }}
                  >
                    <div className="flex flex-col gap-[30px] font-[var(--font-avenir)] text-text-highlight text-[18px] leading-[27px] text-justify">
                      {tab.content}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div key={`content-${animationKey}`}>{activeTab.content}</div>
        ))}

      {/* CSS Animations */}
      <style jsx>{`
        /* The underline reads as a sensor locking onto a target rather than a
           panel sliding: the travel overshoots ~5px and settles back. Width uses a
           plain decelerate curve — letting the bar's length overshoot too would
           read as rubber, not as a lock. Nothing here touches the glow: it stays
           at the bar's own constant 10px. */
        .tab-underline {
          transition:
            transform 280ms cubic-bezier(0.28, 1.25, 0.4, 1),
            width 280ms cubic-bezier(0.16, 1, 0.3, 1);
        }

        @media (prefers-reduced-motion: reduce) {
          .tab-underline {
            transition: none;
          }
        }

        @keyframes contentFadeIn {
          from {
            opacity: 0;
            transform: translateY(15px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes progressBar {
          from {
            width: 0%;
          }
          to {
            width: 100%;
          }
        }

        @keyframes expandWidth {
          from {
            transform: scaleX(0);
          }
          to {
            transform: scaleX(1);
          }
        }

        .content-title {
          animation: contentFadeIn 0.3s ease-out;
        }

        .accent-line {
          animation: expandWidth 0.5s ease-out;
          transform-origin: center;
        }

        .tabs-container:hover .progress-bar {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

export default Tabs;
